import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Play, RotateCcw, ExternalLink, ImageUp, Trash2 } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API, useStudioApi, errMsg } from "@/lib/api";
import { BODY_FONTS, DISPLAY_FONTS, PRESETS, THEME_SECTIONS, loadFont, themeVars, vizOptions, fadeGradient, faceplateClass } from "@/lib/theme";
import { BeatClock } from "@/lib/beatClock";
import { LedVisualizer } from "@/components/listen/LedVisualizer";

const ImageField = ({ label, station, onUpload, onRemove }) => {
  const input = useRef(null);
  return (
    <div className="sm:col-span-2 rounded-lg border border-white/10 bg-black/30 p-3 flex items-center gap-3" data-testid="theme-name-image-field">
      <div className="w-24 h-14 rounded bg-black/60 flex items-center justify-center overflow-hidden shrink-0">
        {station?.has_name_image
          ? <img src={`${API}/station/name_image?v=${station.name_image_version}`} alt="Logo" className="max-w-full max-h-full object-contain" data-testid="theme-name-image-preview" />
          : <span className="text-[0.6rem] font-mono text-[color:var(--lr-muted)]">NO IMAGE</span>}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm">{label}</p>
        <p className="text-xs text-[color:var(--lr-muted)]">Transparent PNG looks best. JPG works too.</p>
      </div>
      <input ref={input} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => { onUpload(e.target.files[0]); e.target.value = ""; }} data-testid="theme-name-image-input" />
      <button onClick={() => input.current.click()} className="rounded-lg border border-white/15 px-3 py-2 text-sm flex items-center gap-1.5 hover:bg-white/10" data-testid="theme-name-image-upload-button"><ImageUp className="w-4 h-4" /> Upload</button>
      {station?.has_name_image && <button onClick={onRemove} className="rounded-lg border border-white/15 px-3 py-2 text-sm text-[color:var(--red)] hover:bg-white/10" data-testid="theme-name-image-remove-button"><Trash2 className="w-4 h-4" /></button>}
    </div>
  );
};

const Field = ({ k, label, type, extra, value, onChange }) => {
  if (type === "color") {
    return (
      <label className="flex items-center gap-3 rounded-lg border border-white/10 bg-black/30 px-3 py-2 cursor-pointer">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-8 h-8 rounded border-0 bg-transparent cursor-pointer" data-testid={`theme-color-${k}`} />
        <span className="text-sm flex-1">{label}</span>
        <span className="font-mono text-xs text-[color:var(--lr-muted)] uppercase">{value}</span>
      </label>
    );
  }
  if (type === "switch") {
    return (
      <label className="flex items-center justify-between rounded-lg border border-white/10 bg-black/30 px-3 py-2.5 cursor-pointer">
        <span className="text-sm">{label}</span>
        <Switch checked={!!value} onCheckedChange={onChange} data-testid={`theme-switch-${k}`} />
      </label>
    );
  }
  if (type === "range") {
    return (
      <div className="rounded-lg border border-white/10 bg-black/30 px-3 py-2.5 flex flex-col gap-2">
        <div className="flex justify-between text-sm"><span>{label}</span><span className="font-mono text-xs text-[color:var(--gold)]">{value}{extra.unit}</span></div>
        <Slider value={[value]} min={extra.min} max={extra.max} step={1} onValueChange={(v) => onChange(v[0])} data-testid={`theme-range-${k}`} />
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="bg-black/30" data-testid={`theme-select-${k}`}><SelectValue /></SelectTrigger>
        <SelectContent>{extra.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`theme-select-${k}-${v}`}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
};

const FontField = ({ id, label, value, fonts, onChange }) => (
  <div className="flex flex-col gap-1.5">
    <Label>{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="bg-black/30" data-testid={`theme-font-${id}`}><SelectValue /></SelectTrigger>
      <SelectContent className="max-h-72">
        {fonts.map((f) => <SelectItem key={f} value={f} style={{ fontFamily: `"${f}"` }} data-testid={`theme-font-option-${f.replace(/ /g, "-")}`}>{f}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const Preview = ({ theme, clock, station }) => (
  <div className="listen-theme dot-grid relative rounded-2xl p-4 border border-white/10 flex flex-col gap-4 overflow-hidden" style={themeVars(theme)} data-testid="theme-preview">
    <div className="absolute inset-x-0 top-0 h-40 pointer-events-none" style={{ background: fadeGradient(theme.fade_strength), opacity: 0.35 }} />
    <LedVisualizer clock={clock} text="Lowride Life Radio" font={theme.display_font} playing={false} opts={vizOptions(theme)}
      imageUrl={station?.has_name_image ? `${API}/station/name_image?v=${station.name_image_version}` : null} className="relative w-full h-[200px]" />
    <div className={`hardware car-deck ${faceplateClass(theme.deck_faceplate)} relative rounded-xl p-3 flex gap-3`} data-testid="theme-preview-deck">
      <div className="lcd rounded-lg p-3 flex-1 min-w-0">
        <p className="text-[0.6rem] tracking-[0.2em] lcd-text">ON AIR · 92 BPM</p>
        <p className="font-display text-2xl leading-tight candy-text truncate">Song title</p>
        <p className="text-xs opacity-70 lcd-text">Artist name</p>
        <div className="h-1 rounded-full bg-white/10 mt-2 overflow-hidden"><div className="h-full w-3/5 lr-progress" /></div>
      </div>
      <span className="hw-btn rounded-lg w-16 flex items-center justify-center"><Play className="w-6 h-6 hw-icon" /></span>
    </div>
    <div className={`hardware car-deck ${faceplateClass(theme.video_faceplate)} relative rounded-xl p-2`} data-testid="theme-preview-video">
      <div className="lcd rounded-md aspect-[16/5] flex items-center justify-center text-xs lcd-text">VIDEO PLAYER</div>
    </div>
    <div className="dash-card relative p-3">
      <p className="font-bold" style={{ color: "var(--t-label)" }}>Recently played song</p>
      <p className="text-xs" style={{ color: "var(--t-muted)" }}>10:42 PM · artist</p>
    </div>
    <div className="bottom-bar relative rounded-lg p-2 flex items-center gap-2">
      <span className="hw-btn rounded-md w-10 h-8 flex items-center justify-center"><Play className="w-4 h-4 hw-icon" /></span>
      <span className="lcd rounded-md flex-1 px-2 py-1 text-xs lcd-text truncate">Artist - Song title</span>
      <Slider defaultValue={[70]} max={100} className="w-20 lr-volume" />
    </div>
  </div>
);

export const ThemePanel = () => {
  const call = useStudioApi();
  const [theme, setTheme] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [station, setStation] = useState(null);
  const [saving, setSaving] = useState(false);
  const clock = useMemo(() => { const c = new BeatClock(); c.setStatus({ id: "preview", bpm: 92, elapsed: 0 }, performance.now()); return c; }, []);

  useEffect(() => {
    [...DISPLAY_FONTS, ...BODY_FONTS].forEach(loadFont);
    axios.get(`${API}/station/status`).then(({ data }) => { setTheme(data.station.theme); setStation(data.station); });
  }, []);

  const uploadNameImage = async (f) => {
    if (!f) return;
    const data = new FormData();
    data.append("file", f);
    try { setStation(await call("post", "/studio/image/name_image", data)); toast.success("Logo image uploaded. It shows when Look is set to 'My logo image'."); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const removeNameImage = async () => {
    try { setStation(await call("delete", "/studio/image/name_image")); toast.success("Logo image removed"); } catch (e) { toast.error(errMsg(e)); }
  };
  const set = (k) => (v) => setTheme((t) => ({ ...t, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { setTheme((await call("put", "/studio/theme", theme)).theme); toast.success("Theme saved. The public page updates within a few seconds."); }
    catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };
  const getDefaults = async () => {
    if (defaults) return defaults;
    const { data } = await axios.get(`${API}/station/theme-defaults`);
    setDefaults(data);
    return data;
  };
  const applyPreset = async (patch) => {
    try { setTheme({ ...(await getDefaults()), ...patch }); toast("Preset loaded. Tweak it, then Save theme."); } catch (e) { toast.error(errMsg(e)); }
  };
  const reset = async () => {
    if (!window.confirm("Reset all colors, fades, visualizer and fonts to the original look?")) return;
    try { setTheme((await call("delete", "/studio/theme")).theme); toast.success("Theme reset to default"); } catch (e) { toast.error(errMsg(e)); }
  };

  if (!theme) return <section className="panel p-5 mt-3 text-sm text-[color:var(--lr-muted)]">Loading theme...</section>;
  return (
    <section className="panel p-5 mt-3 grid grid-cols-1 gap-8 lg:grid-cols-[1.15fr_1fr] [&>*]:min-w-0" data-testid="studio-theme-panel">
      <div className="flex flex-col gap-7">
        <div className="flex flex-col gap-2">
          <span className="eyebrow">Presets</span>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map(([name, patch]) => (
              <button key={name} onClick={() => applyPreset(patch)} className="rounded-full border border-white/15 px-3 py-1.5 text-sm hover:bg-white/10 flex items-center gap-2" data-testid={`theme-preset-${name.replace(/ /g, "-")}`}>
                <span className="w-3 h-3 rounded-full" style={{ background: patch.viz_color || "#ffe600" }} /> {name}
              </button>
            ))}
          </div>
        </div>
        {THEME_SECTIONS.map(([section, fields]) => (
          <div key={section} className="flex flex-col gap-2">
            <span className="eyebrow">{section}</span>
            <div className="grid sm:grid-cols-2 gap-2">
              {fields.filter((f) => { const show = f[4]?.show || (!Array.isArray(f[3]) && f[3]?.show); return !show || show(theme); }).map(([k, label, type, extra]) => (
                type === "image"
                  ? <ImageField key={k} label={label} station={station} onUpload={uploadNameImage} onRemove={removeNameImage} />
                  : <Field key={k} k={k} label={label} type={type} extra={extra} value={theme[k]} onChange={set(k)} />
              ))}
            </div>
          </div>
        ))}
        <div className="flex flex-col gap-3">
          <span className="eyebrow">Fonts</span>
          <div className="grid sm:grid-cols-2 gap-3">
            <FontField id="display" label="Headings & lights (station name, song title)" value={theme.display_font} fonts={DISPLAY_FONTS} onChange={set("display_font")} />
            <FontField id="body" label="Body text" value={theme.body_font} fonts={BODY_FONTS} onChange={set("body_font")} />
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <button onClick={save} disabled={saving} className="rounded-lg bg-[color:var(--red)] px-5 py-2.5 font-bold disabled:opacity-60" data-testid="theme-save-button">{saving ? "Saving..." : "Save theme"}</button>
          <button onClick={reset} className="rounded-lg border border-white/15 px-4 py-2.5 text-sm flex items-center gap-2 hover:bg-white/10" data-testid="theme-reset-button"><RotateCcw className="w-4 h-4" /> Reset to default</button>
          <a href="/" target="_blank" rel="noreferrer" className="rounded-lg border border-white/15 px-4 py-2.5 text-sm flex items-center gap-2 hover:bg-white/10" data-testid="theme-open-public-link"><ExternalLink className="w-4 h-4" /> View public page</a>
        </div>
      </div>
      <div className="flex flex-col gap-2 lg:sticky lg:top-20 self-start w-full">
        <span className="eyebrow">Live preview</span>
        <Preview theme={theme} clock={clock} station={station} />
      </div>
    </section>
  );
};
